import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { newsService } from '@/services/storage';
import type { News } from '@/types';

export default function News() {
  const [, setNews] = useState<News[]>([]);
  const [companyNews, setCompanyNews] = useState<News[]>([]);
  const [industryNews, setIndustryNews] = useState<News[]>([]);

  useEffect(() => {
    const loadNews = async () => {
      const allNews = await newsService.getAll();
      setNews(allNews);
      setCompanyNews(allNews.filter((n) => n.category === 'company'));
      setIndustryNews(allNews.filter((n) => n.category === 'industry'));
    };
    loadNews();
  }, []);

  return (
    <div className="pt-20">
      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              新闻中心
            </h1>
            <p className="text-xl text-blue-100">
              了解瀚昕微最新动态和行业资讯
            </p>
          </div>
        </div>
      </section>

      {/* News List */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="company">
            <TabsList className="mb-8">
              <TabsTrigger value="company">公司动态</TabsTrigger>
              <TabsTrigger value="industry">行业新闻</TabsTrigger>
            </TabsList>

            <TabsContent value="company">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {companyNews.map((item) => (
                  <Link key={item.id} to={`/news/${item.id}`}>
                    <Card className="h-full hover:shadow-lg transition-shadow overflow-hidden">
                      <div className="aspect-video bg-gray-200 relative overflow-hidden">
                        <img
                          src={item.image || 'https://images.unsplash.com/photo-1519389950476-29a8e752cc80?w=400&q=80'}
                          alt={item.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <CardContent className="p-6">
                        <div className="flex items-center text-sm text-gray-500 mb-2">
                          <Calendar className="h-4 w-4 mr-1" />
                          {item.date}
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 text-sm line-clamp-2">
                          {item.summary}
                        </p>
                        <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                          了解详情 <ArrowRight className="ml-1 h-4 w-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
              {companyNews.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  暂无公司动态
                </div>
              )}
            </TabsContent>

            <TabsContent value="industry">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {industryNews.map((item) => (
                  <Link key={item.id} to={`/news/${item.id}`}>
                    <Card className="h-full hover:shadow-lg transition-shadow overflow-hidden">
                      <div className="aspect-video bg-gray-200 relative overflow-hidden">
                        <img
                          src={item.image || 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&q=80'}
                          alt={item.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <CardContent className="p-6">
                        <div className="flex items-center text-sm text-gray-500 mb-2">
                          <Calendar className="h-4 w-4 mr-1" />
                          {item.date}
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 text-sm line-clamp-2">
                          {item.summary}
                        </p>
                        <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                          了解详情 <ArrowRight className="ml-1 h-4 w-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
              {industryNews.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  暂无行业新闻
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
